import type { CurrencyCode } from "./types.js";
import type { PaymentProviderId } from "./payments.js";

/**
 * 市場（Market）——本站最重要的一條規則
 * ---------------------------------------------------------------
 * 這個平台是「多國多市場」，不是「全球單一市場」：
 *
 *   台灣的接案者只看得到台灣的案子，也只能接台灣的案子。
 *   日本、美國、其他國家同理。跨國接案一律禁止。
 *
 * 實作原則（重要，改程式碼時請遵守）：
 *
 *   1. market 是**資料層的強制參數**，不是 UI 篩選器。
 *      每一個查詢函式的第一個參數都是 market，沒有預設值、不能省略。
 *      少寫一次 WHERE market = ? 就是一次跨國資料外洩。
 *
 *   2. 使用者的 market 在註冊時綁定，之後不能自己改
 *      （要改必須重新驗證身分與收款帳戶，走客服流程）。
 *
 *   3. 未登入訪客的 market 由 Cloudflare 的 request.cf.country 決定，
 *      並寫進 URL（/tw/zh-TW/jobs），讓 CDN 能正確分開快取。
 *
 *   4. 使用者的 market 與 URL 的 market 不一致時，一律以帳號的為準並轉址。
 */

export interface MarketConfig {
  /** ISO 3166-1 alpha-2，大寫 */
  code: string;
  /** URL 用的小寫代碼 */
  slug: string;
  /** 市場名稱（英文，顯示時走 i18n key market.<slug>） */
  name: string;
  /** 這個市場預設的介面語言 */
  defaultLocale: string;
  /** 這個市場開放的介面語言 */
  locales: string[];
  /** 案件金額顯示與結帳幣別 */
  currency: CurrencyCode;
  /** 這個市場走哪家金流 */
  paymentProvider: PaymentProviderId;
  /** IANA 時區，用來顯示「當地時間」與案件時間戳 */
  timezone: string;
  /** 是否已開放（false 的市場會顯示 waitlist 頁） */
  live: boolean;
  /**
   * 這個市場最常見的加值稅率（萬分點）。請款單的稅率選項用它。
   *
   * 這個欄位存在的理由：請款單原本寫死「5%（台灣營業稅）」，
   * 那對日本（消費稅 10%）、西班牙（IVA 21%）、英國（VAT 20%）
   * 全部是錯的 —— 而且那不是翻譯能修的東西，錯的是數字本身。
   *
   * 0 表示這個市場沒有單一的全國稅率（例如美國各州不同），
   * 使用者要自己填。
   */
  salesTaxBps: number;
  /** 稅目的名稱，顯示時走 i18n 的 tax.<name> */
  salesTaxName: "vat" | "gst" | "businessTax" | "consumptionTax" | "salesTax";
}

/**
 * 已開放的市場清單。
 * 要新增市場：加一筆設定 → 補 i18n 的 market.<slug> → 確認金流可用。
 */
/*
  ── 全部開放：這是一個國際站 ────────────────────────────────

  任何國家都能註冊、都能用。每個國家是自己的市場（見 marketFor
  的推導），而這七個是人工設定過幣別、稅制、金流的那幾個。

  ── 這裡曾經只開台灣 ────────────────────────────────────────

  上一版把其餘六個設成 live: false，理由是「一個雙邊市集要在每個
  市場各自達到臨界量，先把一個做起來」。那個判斷在策略上成立，
  但它的實際效果比預期更寬：日本、美國、英國、西班牙、新加坡、
  澳洲的訪客會被 302 到台灣，而且**註冊不了** —— 而越南、韓國、
  法國那些自動推導的國家反而開著。也就是說，被關掉的恰好是準備
  最完整的六個。

  站方的決定是：國際站，任何國家都能註冊使用。

  ── 國家怎麼決定 ────────────────────────────────────────────

  連線 IP（Cloudflare 的 cf.country），不是表單上的選擇。
  見 services/identity 的註冊處理。

  ── live: false 還留著這個開關 ──────────────────────────────

  真的要關掉某個市場的那一天（例如金流出事），把它改回 false
  就會 302 到 FALLBACK_MARKET，而且保留使用者要的語系。
*/
export const MARKETS: Record<string, MarketConfig> = {
  TW: {
    code: "TW",
    slug: "tw",
    name: "Taiwan",
    defaultLocale: "zh-TW",
    locales: ["zh-TW", "en"],
    currency: "TWD",
    paymentProvider: "ecpay",
    timezone: "Asia/Taipei",
    live: true,
    salesTaxBps: 500,
    salesTaxName: "businessTax",
  },
  JP: {
    code: "JP",
    slug: "jp",
    name: "Japan",
    defaultLocale: "ja",
    locales: ["ja", "en"],
    currency: "USD",
    paymentProvider: "paypal",
    timezone: "Asia/Tokyo",
    live: true,
    salesTaxBps: 1000,
    salesTaxName: "consumptionTax",
  },
  US: {
    code: "US",
    slug: "us",
    name: "United States",
    defaultLocale: "en",
    locales: ["en", "es"],
    currency: "USD",
    paymentProvider: "paypal",
    timezone: "America/New_York",
    live: true,
    salesTaxBps: 0,
    salesTaxName: "salesTax",
  },
  GB: {
    code: "GB",
    slug: "gb",
    name: "United Kingdom",
    defaultLocale: "en",
    locales: ["en"],
    currency: "USD",
    paymentProvider: "paypal",
    timezone: "Europe/London",
    live: true,
    salesTaxBps: 2000,
    salesTaxName: "vat",
  },
  ES: {
    code: "ES",
    slug: "es",
    name: "Spain",
    defaultLocale: "es",
    locales: ["es", "en"],
    currency: "USD",
    paymentProvider: "paypal",
    timezone: "Europe/Madrid",
    live: true,
    salesTaxBps: 2100,
    salesTaxName: "vat",
  },
  SG: {
    code: "SG",
    slug: "sg",
    name: "Singapore",
    defaultLocale: "en",
    locales: ["en", "zh-TW"],
    currency: "USD",
    paymentProvider: "paypal",
    timezone: "Asia/Singapore",
    live: true,
    salesTaxBps: 900,
    salesTaxName: "gst",
  },
  AU: {
    code: "AU",
    slug: "au",
    name: "Australia",
    defaultLocale: "en",
    locales: ["en"],
    currency: "USD",
    paymentProvider: "paypal",
    timezone: "Australia/Sydney",
    live: true,
    salesTaxBps: 1000,
    salesTaxName: "gst",
  },
};

/** 找不到來訪國家時的落點。這只在拿不到國碼時才會用到。 */
export const FALLBACK_MARKET = "TW";

/**
 * 全世界都以國家隔離
 * ═══════════════════════════════════════════════════════════════
 *
 * 在此之前只有七個「開放市場」，其餘國家一律落到 US ——
 * 於是法國的訪客看到的是美國的案件與接案者。對一個以
 * 「只跟同一個司法管轄區的人成交」為核心承諾的平台來說，
 * 那等於承諾在 190 個國家都不成立。
 *
 * 現在每一個 ISO 國家都是一個市場。上面 MARKETS 裡那七個是
 * **有人工設定**的（在地幣別、時區、金流商、稅率、開放語言），
 * 其餘國家用下面的規則推導出設定。
 *
 * ── 推導出來的市場一樣是真的市場 ────────────────────────────
 *
 * 它不是「次等市場」：法國的使用者註冊時綁 FR，只看得到 FR 的
 * 案件，也只能跟 FR 的人成交。差別只在顯示層的細節（幣別、時區）
 * 用的是通則而不是為它調過的值。
 *
 * ── 代價要講明白 ────────────────────────────────────────────
 *
 * 剛開站時這些市場全都是空的。法國訪客會看到一個沒有案件的網站，
 * 而不是滿滿的美國案件。那是誠實的：給他看美國的案子，他點進去
 * 才發現不能投標，比空的更糟。
 */
/** ISO 3166-1 alpha-2 的現行分配碼。用來擋掉 /xx/ 這種不存在的市場前綴。 */
const ISO_COUNTRIES = new Set([
  "AD", "AE", "AF", "AG", "AI", "AL", "AM", "AO", "AQ", "AR", "AS", "AT", "AU", "AW", "AX", "AZ",
  "BA", "BB", "BD", "BE", "BF", "BG", "BH", "BI", "BJ", "BL", "BM", "BN", "BO", "BQ", "BR", "BS",
  "BT", "BV", "BW", "BY", "BZ", "CA", "CC", "CD", "CF", "CG", "CH", "CI", "CK", "CL", "CM", "CN",
  "CO", "CR", "CU", "CV", "CW", "CX", "CY", "CZ", "DE", "DJ", "DK", "DM", "DO", "DZ", "EC", "EE",
  "EG", "EH", "ER", "ES", "ET", "FI", "FJ", "FK", "FM", "FO", "FR", "GA", "GB", "GD", "GE", "GF",
  "GG", "GH", "GI", "GL", "GM", "GN", "GP", "GQ", "GR", "GS", "GT", "GU", "GW", "GY", "HK", "HM",
  "HN", "HR", "HT", "HU", "ID", "IE", "IL", "IM", "IN", "IO", "IQ", "IR", "IS", "IT", "JE", "JM",
  "JO", "JP", "KE", "KG", "KH", "KI", "KM", "KN", "KP", "KR", "KW", "KY", "KZ", "LA", "LB", "LC",
  "LI", "LK", "LR", "LS", "LT", "LU", "LV", "LY", "MA", "MC", "MD", "ME", "MF", "MG", "MH", "MK",
  "ML", "MM", "MN", "MO", "MP", "MQ", "MR", "MS", "MT", "MU", "MV", "MW", "MX", "MY", "MZ", "NA",
  "NC", "NE", "NF", "NG", "NI", "NL", "NO", "NP", "NR", "NU", "NZ", "OM", "PA", "PE", "PF", "PG",
  "PH", "PK", "PL", "PM", "PN", "PR", "PS", "PT", "PW", "PY", "QA", "RE", "RO", "RS", "RU", "RW",
  "SA", "SB", "SC", "SD", "SE", "SG", "SH", "SI", "SJ", "SK", "SL", "SM", "SN", "SO", "SR", "SS",
  "ST", "SV", "SX", "SY", "SZ", "TC", "TD", "TF", "TG", "TH", "TJ", "TK", "TL", "TM", "TN", "TO",
  "TR", "TT", "TV", "TW", "TZ", "UA", "UG", "UM", "US", "UY", "UZ", "VA", "VC", "VE", "VG", "VI",
  "VN", "VU", "WF", "WS", "YE", "YT", "ZA", "ZM", "ZW",
]);

/** 主要國家的時區，用來顯示「當地時間」。表裡沒有的用 UTC。 */
const COUNTRY_TIMEZONE: Record<string, string> = {
  AE: "Asia/Dubai", AR: "America/Argentina/Buenos_Aires", AT: "Europe/Vienna",
  BD: "Asia/Dhaka", BE: "Europe/Brussels", BR: "America/Sao_Paulo",
  CA: "America/Toronto", CH: "Europe/Zurich", CL: "America/Santiago",
  CN: "Asia/Shanghai", CO: "America/Bogota", CZ: "Europe/Prague",
  DE: "Europe/Berlin", DK: "Europe/Copenhagen", EG: "Africa/Cairo",
  FI: "Europe/Helsinki", FR: "Europe/Paris", GR: "Europe/Athens",
  HK: "Asia/Hong_Kong", HU: "Europe/Budapest", ID: "Asia/Jakarta",
  IE: "Europe/Dublin", IL: "Asia/Jerusalem", IN: "Asia/Kolkata",
  IT: "Europe/Rome", KE: "Africa/Nairobi", KR: "Asia/Seoul",
  MA: "Africa/Casablanca", MO: "Asia/Macau", MX: "America/Mexico_City",
  MY: "Asia/Kuala_Lumpur", NG: "Africa/Lagos", NL: "Europe/Amsterdam",
  NO: "Europe/Oslo", NZ: "Pacific/Auckland", PE: "America/Lima",
  PH: "Asia/Manila", PK: "Asia/Karachi", PL: "Europe/Warsaw",
  PT: "Europe/Lisbon", RO: "Europe/Bucharest", RU: "Europe/Moscow",
  SA: "Asia/Riyadh", SE: "Europe/Stockholm", TH: "Asia/Bangkok",
  TR: "Europe/Istanbul", UA: "Europe/Kyiv", VN: "Asia/Ho_Chi_Minh",
  ZA: "Africa/Johannesburg",
};

/**
 * 這個國家的預設介面語言。
 *
 * 只列有介面版本的語言（七種）。名單外的國家一律英文 ——
 * 給法國人一個半翻的德文介面，比給他英文糟。
 */
const COUNTRY_LOCALE: Record<string, string> = {
  // 繁體中文
  TW: "zh-TW", HK: "zh-TW", MO: "zh-TW",
  // 日文
  JP: "ja",
  // 西班牙文（西班牙 + 拉丁美洲）
  ES: "es", MX: "es", AR: "es", CO: "es", CL: "es", PE: "es", VE: "es", EC: "es",
  GT: "es", CU: "es", BO: "es", DO: "es", HN: "es", PY: "es", SV: "es", NI: "es",
  CR: "es", PA: "es", UY: "es", GQ: "es", PR: "es",
  // 法文（法國 + 法語非洲 + 加勒比海）
  FR: "fr", BE: "fr", LU: "fr", MC: "fr", GF: "fr", GP: "fr", MQ: "fr", RE: "fr",
  YT: "fr", NC: "fr", PF: "fr", WF: "fr", BL: "fr", MF: "fr", PM: "fr",
  CI: "fr", SN: "fr", ML: "fr", BF: "fr", NE: "fr", TD: "fr", GN: "fr", BJ: "fr",
  TG: "fr", CG: "fr", CD: "fr", GA: "fr", CM: "fr", MG: "fr", CF: "fr", DJ: "fr",
  KM: "fr", HT: "fr", BI: "fr", RW: "fr",
  // 德文
  DE: "de", AT: "de", LI: "de", CH: "de",
  // 韓文
  KR: "ko",
  // 葡萄牙文
  BR: "pt-BR", PT: "pt-BR", AO: "pt-BR", MZ: "pt-BR", CV: "pt-BR", GW: "pt-BR",
  ST: "pt-BR", TL: "pt-BR",

  /*
    2026-08 新增的十種語言。

    這一整張表決定「一個國家的使用者第一次進站看到什麼語言」。
    在這之前它只涵蓋七種，其餘兩百多個國家一律英文 ——
    當時的判斷是對的：一種語言等於一千六百條要有人寫、有人
    維護的字串，而半吊子的翻譯比誠實的英文糟。

    加上建置期的機器翻譯之後，那個成本結構變了，所以名單跟著長。
    但原則沒變：名單外的國家仍然一律英文，而不是硬塞一個
    沒有人校對過的語言。
  */

  /*
    義大利文。

    瑞士**不**列在這裡：它的官方語言有德、法、義三種，
    而義大利語人口只佔約 8%。預設給多數人（德語 63%），
    其他人自己切 —— 一個錯的預設比沒有預設糟。
  */
  IT: "it", SM: "it", VA: "it",
  // 荷蘭文
  NL: "nl", SR: "nl", AW: "nl", CW: "nl", SX: "nl",
  // 波蘭文
  PL: "pl",
  // 土耳其文
  TR: "tr",
  // 印尼文
  ID: "id",
  // 泰文
  TH: "th",
  // 越南文
  VN: "vi",
  // 俄文（俄羅斯與幾個以俄文為通用語的國家）
  RU: "ru", BY: "ru", KZ: "ru", KG: "ru", TJ: "ru",
  // 阿拉伯文
  SA: "ar", AE: "ar", EG: "ar", QA: "ar", KW: "ar", BH: "ar", OM: "ar", JO: "ar",
  LB: "ar", IQ: "ar", LY: "ar", TN: "ar", DZ: "ar", MA: "ar", SD: "ar", YE: "ar",
  SY: "ar", PS: "ar", MR: "ar",
  /*
    印地文**不**設成印度的預設。

    印度的接案與資訊產業實際的工作語言是英文 —— 案件描述、
    履歷、與海外客戶的往來都是。把預設設成印地文，等於
    在第一眼就把這個平台定位成「本地小案子」。

    hi.ts 仍然存在，語言選單裡選得到。這是「有得選」
    和「預設就是它」的差別。
  */
};
/**
 * 一個市場用什麼幣別。
 *
 * ── 現況：只有台灣是在地幣別，其餘六個市場都是美元 ──────────
 *
 * 這不是設計，是還沒做完。
 *
 * 這裡原本的說明是「金額一律以 USD 的最小單位儲存，各國幣別只是
 * 顯示層的換算」—— 而 shared/currency.ts 的說明是**相反的**：
 * 「金額存的是該市場自己的幣別，台灣存台幣、日本存日圓」，
 * 而且它明確寫著舊的 USD 設計已經被移除，因為它造成過一個
 * 「台灣的每一個金額都被乘上 32.5 倍」的錯誤。
 *
 * 兩份說明同時存在了很久，而程式碼照著舊的那一份做。
 *
 * ── 現在的後果 ──────────────────────────────────────────────
 *
 *   · 日本的發案表單標「預算下限（USD）」
 *   · tax/jp.ts 用日圓算稅，但同一個人的案件與請款單是美元 ——
 *     財務頁靠「稅務規制的幣別」判斷本國收入，於是永遠是 0
 *   · fees.ts 裡 JPY／EUR／GBP／SGD／AUD 的會員費與預算下限
 *     全部是死碼：寫了，但沒有一個市場會用到
 *
 * ── 為什麼還沒改 ────────────────────────────────────────────
 *
 * 因為改的不是這一行，是資料庫裡已經存進去的每一筆金額的意思。
 * 一個日本案件現在存 250000（代表 US$2,500），把市場幣別換成 JPY
 * 之後同一個數字會顯示成 ¥2,500 —— 相差一百多倍，而且不會報錯。
 *
 * 要做的是一次遷移，而遷移之前要先決定三件事：既有金額用哪一天的
 * 匯率換、之後的匯率從哪裡來、以及簽約當下要不要把匯率快照存進
 * 合約（不存的話，一份跨年的合約金額會自己浮動）。
 *
 * 那三個決定不是程式問題。在有答案之前，這裡維持現狀 ——
 * 但把現狀寫清楚，不要再讓下一個人以為它是設計。
 */
function currencyFor(code: string): CurrencyCode {
  return code === "TW" ? "TWD" : "USD";
}

/**
 * 國家名稱。用 Intl 產生，不自己維護一張 249 筆的名稱表 ——
 * 那張表會過時，而 Intl 跟著執行環境的 CLDR 更新。
 */
function displayName(code: string): string {
  try {
    return new Intl.DisplayNames(["en"], { type: "region" }).of(code) ?? code;
  } catch {
    return code;
  }
}

/** 為沒有人工設定的國家推導一份市場設定 */
function deriveMarket(code: string): MarketConfig {
  const locale = COUNTRY_LOCALE[code] ?? "en";
  return {
    code,
    slug: code.toLowerCase(),
    name: displayName(code),
    defaultLocale: locale,
    // 一律加上英文 —— 推導出來的市場沒有在地化的介面，英文是共通的退路
    locales: locale === "en" ? ["en"] : [locale, "en"],
    currency: currencyFor(code),
    paymentProvider: "paypal",
    timezone: COUNTRY_TIMEZONE[code] ?? "UTC",
    live: true,
    salesTaxBps: 0,
    salesTaxName: "salesTax",
  };
}

/** 推導出來的設定會被記住，同一個國碼不用重算 */
const derived = new Map<string, MarketConfig>();

function marketFor(code: string): MarketConfig | null {
  const upper = code.toUpperCase();
  const curated = MARKETS[upper];

  /*
    人工設定過的市場，live 說了算。
    ═════════════════════════════════════════════════════════════

    這裡原本是 `if (curated?.live) return curated;` 然後往下走推導 ——
    結果是把一個**刻意下線**的市場，用自動推導又救了回來
    （下面那段只看國碼是不是有效的 ISO，不看有沒有被下線）。
    也就是說 live: false 完全沒有作用：真的要把某個市場關掉的那一天
    才會發現關不掉，而那一天通常是出了事才需要關。

    測試也沒抓到 —— 那條測試寫成「如果找得到未上線的市場才斷言」，
    而目前每一個都是上線的，所以它從來沒有真的跑過。
  */
  if (curated) return curated.live ? curated : null;

  if (!ISO_COUNTRIES.has(upper)) return null;

  let cached = derived.get(upper);
  if (!cached) {
    cached = deriveMarket(upper);
    derived.set(upper, cached);
  }
  return cached;
}

export const MARKET_CODES = Object.keys(MARKETS);
export const MARKET_SLUGS = Object.values(MARKETS).map((m) => m.slug);

/**
 * 這個國碼是不是一個市場。
 *
 * 現在只要是有效的 ISO 國碼就是 —— 全世界都以國家隔離，
 * 沒有「不支援的國家」，只有「還沒有人的市場」。
 */
export function isSupportedMarket(countryCode: string | undefined | null): boolean {
  if (!countryCode) return false;
  return marketFor(countryCode) !== null;
}

/** 取得市場設定；國碼無效時回傳 fallback */
export function getMarket(countryCode: string | undefined | null): MarketConfig {
  return (countryCode ? marketFor(countryCode) : null) ?? MARKETS[FALLBACK_MARKET];
}

/** 從 URL 的小寫 slug 取回市場，找不到回 null（呼叫端要負責轉址） */
export function marketFromSlug(slug: string | undefined | null): MarketConfig | null {
  if (!slug || slug.length !== 2) return null;
  return marketFor(slug);
}

/**
 * 這個市場支不支援這個語言；不支援就退回市場預設語言。
 * 用途：使用者帶著 /tw/ja/jobs 進來時，ja 不在台灣市場開放語言內 → 轉去 /tw/zh-TW/jobs。
 */
export function resolveLocale(market: MarketConfig, requested: string | undefined | null): string {
  if (requested && market.locales.includes(requested)) return requested;
  return market.defaultLocale;
}

/** 組出市場前綴的路徑：buildPath(twMarket, "zh-TW", "/jobs") → "/tw/zh-TW/jobs" */
export function buildPath(market: MarketConfig, locale: string, path: string): string {
  const clean = path.startsWith("/") ? path : `/${path}`;
  return `/${market.slug}/${locale}${clean === "/" ? "" : clean}`;
}

/**
 * 跨市場交易的政策
 * ═══════════════════════════════════════════════════════════════
 *
 * ── 為什麼是一個設定，不是一行 === ──────────────────────────
 *
 * 「等市場成熟了再開放跨國接案」是一個會真的發生的決定，而它
 * 發生的時候，改的人不會是今天寫這段的人。
 *
 * 寫成 `a === b` 的話，那個人要自己找出所有該改的地方 —— 而這
 * 個函式的歷史正好證明那有多容易出錯：它曾經**整庫零個呼叫端**，
 * 註解卻寫著「改這一個函式即可」。照著做的人會改完、看到測試
 * 全過、然後以為開通了，實際上什麼都沒發生。
 *
 * 所以把它變成一份可以讀、可以測、可以逐步放寬的設定。
 *
 * ── 三種模式 ────────────────────────────────────────────────
 *
 *   isolated  只有同市場能交易。今天的行為。
 *   groups    同一個「集團」內的市場互通。例如歐盟內部，
 *             或新加坡與馬來西亞。集團外仍然隔離。
 *   open      全世界互通。
 *
 * ── 但改這一個值**不足以**開放跨國 ──────────────────────────
 *
 * 下面的 crossMarketBlockers() 會擋住那些還沒準備好的組合。
 * 那不是保守，是因為以下四件事會直接寫進合約與發票：
 *
 *   幣別    案件用哪一邊的幣別計價
 *   金流    TW 走綠界，而綠界收不了美國發行的卡
 *   稅      TW 5%、US 0% —— 誰的稅率算數
 *   法律    合約範本目前假設雙方同國
 *
 * 前兩件這裡驗得出來（兩邊的 currency 與 paymentProvider 是資料）。
 * 後兩件驗不出來，所以只能在放行時大聲說出來 —— 見
 * CROSS_MARKET_UNRESOLVED。
 */
export type MarketPolicyMode = "isolated" | "groups" | "open";

export interface MarketPolicy {
  mode: MarketPolicyMode;
  /**
   * groups 模式下的互通集團。
   *
   * 一個市場可以屬於多個集團 —— 例如新加坡同時在「東南亞」
   * 與「英語圈」裡。只要有任何一個集團同時包含兩邊就算通。
   */
  groups: Record<string, string[]>;
}

/**
 * 目前的政策。
 *
 * 要開放跨國接案時改這裡 —— 而且要先讀完 crossMarketBlockers()
 * 上面那段說明。改完之後 npm test 會告訴你哪些既有的假設不成立了。
 */
export const MARKET_POLICY: MarketPolicy = {
  mode: "isolated",

  /*
    先寫好但還沒啟用（mode 是 isolated，所以這張表現在不生效）。

    列出來不是為了「準備好了」，是為了讓「開放哪些」變成一個
    可以討論的具體清單，而不是一個抽象的開關。每一組都要
    通過 crossMarketBlockers() 才會真的放行。
  */
  groups: {
    // 同幣別、同金流、同語言圈 —— 技術上最接近可行的一組
    anglosphere: ["US", "GB", "AU", "CA", "NZ", "IE"],
    // 歐元區的一部分。稅務規則（跨境 VAT / 逆向課稅）仍未解決
    eurozone: ["DE", "FR", "NL", "IT", "ES", "IE", "AT", "BE", "PT", "FI"],
    // 星馬。地理與語言最接近，實務上本來就常互相接案
    seaCore: ["SG", "MY"],
  },
};

/**
 * 這一對市場之間，還有哪些技術上的障礙沒解決。
 *
 * 回傳空陣列代表「資料層面沒問題」——**不代表商業與法律上可以**。
 * 稅務歸屬與合約適用法律驗不出來，見 CROSS_MARKET_UNRESOLVED。
 */
export function crossMarketBlockers(a: MarketConfig, b: MarketConfig): string[] {
  const blockers: string[] = [];

  if (a.currency !== b.currency) {
    /*
      不同幣別不是不能做，是「還沒決定用哪一邊」。

      而那個決定不能延後到成交之後 —— 提案的金額在送出的那一刻
      就要有幣別，否則發案方看到的數字和接案者輸入的不是同一個。
    */
    blockers.push(`currency:${a.currency}/${b.currency}`);
  }

  if (a.paymentProvider !== b.paymentProvider) {
    /*
      金流不同的實際後果：綠界收不了美國發行的卡。

      這裡擋的是會員費 —— 平台不碰雙方之間的錢，所以真正卡住的
      不是交易本身，而是「這個人要用哪個金流付會員費」。
      但他的市場決定了那件事，而跨市場媒合會讓那個假設鬆掉。
    */
    blockers.push(`payment:${a.paymentProvider}/${b.paymentProvider}`);
  }

  return blockers;
}

/**
 * 驗不出來的那兩件事。
 *
 * 放在這裡而不是註解裡，是為了讓它出現在型別與搜尋結果中 ——
 * 一段藏在函式上方的警告，會被下一個急著上線的人滑過去。
 */
export const CROSS_MARKET_UNRESOLVED = [
  "salesTax: 兩邊稅率不同時，發票開誰的稅？（TW 5%、US 0%）",
  "governingLaw: 合約範本假設雙方同國，跨國時適用哪一國法律？",
] as const;

/**
 * 兩個市場能不能交易。
 *
 * 這是**唯一**的判斷點 —— gateway 呼叫它，任何新的呼叫端也應該
 * 呼叫它而不是自己比較市場代碼。
 */
export function canTransact(
  clientMarket: string,
  freelancerMarket: string,
  /*
    政策可以注入，預設是上面那個常數。

    這不是為了讓執行期可以換政策 —— 是為了讓「改 mode 就會開放」
    這句話**可以被測試**。這個函式的歷史正好說明為什麼那很重要：
    它曾經整庫零個呼叫端，而註解卻寫著「改這一個函式即可」。
    一句沒有測試撐著的承諾，和沒有承諾一樣。
  */
  policy: MarketPolicy = MARKET_POLICY,
): boolean {
  const a = clientMarket.toUpperCase();
  const b = freelancerMarket.toUpperCase();

  // 同市場永遠可以，不管政策是什麼
  if (a === b) return true;
  if (policy.mode === "isolated") return false;

  if (policy.mode === "groups") {
    const sameGroup = Object.values(policy.groups).some(
      (members) => members.includes(a) && members.includes(b),
    );
    if (!sameGroup) return false;
  }

  /*
    政策說可以之後，還要過技術檢查。

    順序是刻意的：政策是商業決定，技術檢查是「那個決定現在
    做不做得到」。反過來的話，一個幣別不同的組合會因為技術
    原因被擋掉，而讀程式碼的人會以為是政策不允許。
  */
  /*
    ── 已下線的市場一律不能交易 ────────────────────────────────

    這裡本來是 crossMarketBlockers(getMarket(a), getMarket(b))，
    而 getMarket **永遠回傳一個市場** —— 找不到就回 fallback。

    把市場關掉之後那件事變成一個漏洞：canTransact("TW", "US") 會
    在內部變成 canTransact("TW", "TW")（因為 US 下線了、退回台灣），
    幣別與金流當然一樣，於是它回 true。

    「台灣能不能跟美國交易」被悄悄換成「台灣能不能跟台灣交易」。

    marketFor 對下線的市場回 null —— 那才是這裡要問的問題。
  */
  const marketA = marketFor(a);
  const marketB = marketFor(b);
  if (!marketA || !marketB) return false;

  return crossMarketBlockers(marketA, marketB).length === 0;
}


/**
 * 站上實際服務得到的語言
 * ═══════════════════════════════════════════════════════════════
 *
 * 這是「網址走得到」的那一份清單 —— 由各市場的 locales 聯集而成。
 *
 * ── 為什麼要算進 COUNTRY_LOCALE ─────────────────────────────
 *
 * 原本只算 MARKETS（七個精選市場）的聯集，結果是 [en, es, ja, zh-TW]
 * 四種。但這個平台後來對**全世界**開放，兩百多個市場由
 * deriveMarket() 產生，它們的語言來自 COUNTRY_LOCALE ——
 * 而那張表現在涵蓋十七種語言。
 *
 * 只算 MARKETS 的後果是安靜的：isSupportedLocale("fr") 回 false，
 * 於是註冊時法國使用者選的語言**在寫入那一刻被丟掉**（存成 null），
 * 而寄信時就退回英文。畫面是法文的，信是英文的，
 * 而沒有任何錯誤訊息說明為什麼。
 */
/**
 * 站上有字典、使用者選得到的每一種語言。
 *
 * ── 為什麼這一份要手寫，而不是從別的東西算出來 ──────────────
 *
 * 因為它就是**定義**：apps/web/src/i18n/dictionaries/ 裡有幾本字典，
 * 這裡就有幾個代碼。apps/web 的 config.ts 直接 import 這一份，
 * 所以兩邊不可能漂移。
 *
 * 之前它是從 MARKETS 的 locales 算出來的，而那只涵蓋七個精選市場 ——
 * 於是 isSupportedLocale("fr") 回 false，法國使用者在註冊時選的
 * 語言在寫入那一刻就被丟掉，寄信時退回英文。畫面是法文的，
 * 信是英文的，而沒有任何錯誤訊息說明為什麼。
 *
 * 印地文是另一個例子：印度的預設是英文（當地接案產業的工作語言），
 * 所以 hi 不是任何市場的預設 —— 但使用者在語言選單裡選得到它。
 * 用「算出來」的作法會漏掉這一種。
 */
export const SITE_LOCALES = [
  "en",
  "zh-TW",
  "ja",
  "ko",
  "es",
  "fr",
  "de",
  "pt-BR",
  "it",
  "nl",
  "pl",
  "tr",
  "id",
  "th",
  "vi",
  "ru",
  "ar",
  "hi",
] as const;

export type SiteLocale = (typeof SITE_LOCALES)[number];

export const SUPPORTED_LOCALES = SITE_LOCALES as readonly string[];

/** 這個字串是不是我們真的服務得到的語言 */
export function isSupportedLocale(value: string | null | undefined): boolean {
  return typeof value === "string" && SUPPORTED_LOCALES.includes(value);
}

/**
 * 寄信要用哪一種語言。
 *
 * 順序：使用者選過的 → 市場的預設 → 英文。
 *
 * 中間那一層很重要：一個沒選過語言的台灣使用者應該收到中文信，
 * 而不是英文信。退回英文只在「連市場都認不出來」時發生，
 * 而那代表資料有問題，不是使用者的偏好。
 */
export function emailLocale(userLocale: string | null | undefined, marketCode: string): string {
  if (isSupportedLocale(userLocale)) return userLocale as string;

  /*
    用 getMarket() 而不是 MARKETS[code]。

    MARKETS 只有七個精選市場；其餘兩百多個由 deriveMarket() 產生。
    直接查表的話，一個法國使用者（FR 不在 MARKETS 裡）會拿不到
    市場預設，直接掉到英文 —— 即使我們有法文的信件文案。

    getMarket() 兩種都認得。
  */
  /*
    ── 認不出來的市場要退回英文，不是退回 fallback 市場 ──────────

    這裡本來用 getMarket()，而它永遠不回 null —— 找不到就回
    FALLBACK_MARKET。那在七個市場都上線的時候沒有差別。

    但 FALLBACK_MARKET 現在是台灣（只開一個市場），於是一個
    國別認不出來的人會收到**中文**信。他很可能讀不懂。

    「這個人在哪個市場」和「用什麼語言寫信給他」是兩件事：
    前者需要一個保證有值的答案（呼叫端要拿它去查東西），
    後者在不知道的時候該退回英文。
  */
  const market = marketCode && marketCode.length === 2 ? marketFor(marketCode) : null;
  if (market && isSupportedLocale(market.defaultLocale)) return market.defaultLocale;
  return "en";
}
